import { useQuery } from '@tanstack/react-query';
import { projectsAPI, contributorAPI } from '../../services/api';
import { normalizeContributorRows } from '../../utils/contributorChartRows';
import { projectPageKeys } from '../../query/queryKeys';

/**
 * Project detail DORA metrics and contributor analytics (TanStack Query).
 */
export function useProjectDetailDoraAndContributors(
  orgId,
  streamId,
  projectId,
  {
    deploymentWindowDays,
    leadTimeWindowDays,
    churnWindowDays,
    contributorsTimeframe,
    contributorsStatus,
  }
) {
  const enabled = !!orgId && !!streamId && !!projectId;

  const deploymentDoraQuery = useQuery({
    queryKey: projectPageKeys.dora(orgId, streamId, projectId, deploymentWindowDays),
    queryFn: () => projectsAPI.getDora(orgId, streamId, projectId, deploymentWindowDays),
    enabled,
    staleTime: 60_000,
  });

  const leadTimeDoraQuery = useQuery({
    queryKey: projectPageKeys.dora(orgId, streamId, projectId, leadTimeWindowDays),
    queryFn: () => projectsAPI.getDora(orgId, streamId, projectId, leadTimeWindowDays),
    enabled,
    staleTime: 60_000,
  });

  const churnQuery = useQuery({
    queryKey: projectPageKeys.churn(orgId, streamId, projectId, churnWindowDays),
    queryFn: () => projectsAPI.getChurn(orgId, streamId, projectId, churnWindowDays),
    enabled,
    staleTime: 60_000,
  });

  const contributorsQuery = useQuery({
    queryKey: projectPageKeys.contributorAnalytics(
      orgId,
      streamId,
      projectId,
      contributorsTimeframe,
      contributorsStatus
    ),
    queryFn: () =>
      contributorAPI.getByProject(orgId, streamId, projectId, {
        timeframe: contributorsTimeframe,
        status: contributorsStatus,
      }),
    enabled,
    staleTime: 60_000,
    select: (list) => normalizeContributorRows(Array.isArray(list) ? list : []),
  });

  const deploymentDora = deploymentDoraQuery.data?.dora ?? null;
  const deploymentDoraMeta = deploymentDoraQuery.data?.meta ?? null;
  const deploymentLoading = deploymentDoraQuery.isPending;
  const leadTimeDora = leadTimeDoraQuery.data?.dora ?? null;
  const leadTimeDoraMeta = leadTimeDoraQuery.data?.meta ?? null;
  const leadTimeLoading = leadTimeDoraQuery.isPending;
  const churnData = churnQuery.data ?? null;
  const churnLoading = churnQuery.isPending;
  const projectContributorRows = contributorsQuery.data ?? [];
  const projectContributorsLoading = contributorsQuery.isPending;

  return {
    deploymentDora,
    deploymentDoraMeta,
    deploymentLoading,
    leadTimeDora,
    leadTimeDoraMeta,
    leadTimeLoading,
    churnData,
    churnLoading,
    projectContributorRows,
    projectContributorsLoading,
  };
}
